import React, { useState } from 'react'
import { flowRight } from 'lodash'
import { graphql } from '@apollo/client/react/hoc'
import { fetchPolicy } from 'lib/apolloFetchPolicy'
import { EVENT_TYPE } from 'lib/utils/store/event'

import AppContext from 'components/AppContext'
import AttachmentsLogic from 'components/Attachments/AttachmentsLogic'
import LoadingIndicator from 'components/LoadingIndicator'
import EventForm from './EventForm'
import ErrorComponent from 'components/Errors/ErrorComponent'
import InspectionEventForm from './InspectionEventForm/InspectionEventForm'
import RefillEventForm from './RefillEventForm/RefillEventForm'

import getEvent from 'graphql/event/getEvent.gql'

export const EventFormLogicContext = React.createContext()

const EventFormLogic = ({
    vehicleId,
    renderButtonsPortalTarget,
    onCancel,
    data,
    formMode,
    initEvent,
}) => {
    const { loading, error, event } = data || {}

    // The base data is the foundation for EventForm event data,
    // whereas formInitEventAdditionalData can be used to set any additional data before opening form.
    const formInitEventBaseData = { ...initEvent, ...(event || {}) }
    const [formInitEventAdditionalData, setFormInitEventAdditionalData] = useState({})
    const [formLogicState, setFormLogicState] = useState({})
    const [dirty, setDirty] = useState(false)

    if (loading) return <LoadingIndicator />
    if (error) return <ErrorComponent error={error} />

    const writeEventFormState = (values) => {
        setFormLogicState(prevState => {
            return { ...prevState, ...values }
        })
        setDirty(true)
    }

    const setEventType = (type, values = {}) => {
        setFormInitEventAdditionalData(prevState => {
            return { ...prevState, ...values, type: type }
        })
        setDirty(true)
    }

    const writeEventFormInitialEventValues = (values) => {
        setFormInitEventAdditionalData(prevState => {
            return { ...prevState, ...values }
        })
        setDirty(true)
    }

    const initEventCombined = { ...formInitEventBaseData, ...formInitEventAdditionalData }

    const eventType = initEventCombined.type

    const renderEventForm = (formLogicState) => {
        switch (eventType) {
        case EVENT_TYPE.INSPECTION:
            return (
                <EventFormLogicContext.Provider value={formLogicState}>
                    <InspectionEventForm vehicleId={vehicleId} />
                </EventFormLogicContext.Provider>
            )
        case EVENT_TYPE.REFILL:
            return (
                <EventFormLogicContext.Provider value={formLogicState}>
                    <RefillEventForm vehicleId={vehicleId} />
                </EventFormLogicContext.Provider>
            )
        default:
            return (
                <EventFormLogicContext.Provider value={formLogicState}>
                    <EventForm />
                </EventFormLogicContext.Provider>
            )
        }
    }

    return (
        <AppContext context="eventForm">
            <AttachmentsLogic
                vehicleId={vehicleId}
                initAttachments={initEventCombined.attachments || []}
            >
                {() => (
                    renderEventForm({
                        initEvent: initEventCombined,
                        setEventType,
                        writeEventFormState,
                        writeEventFormInitialEventValues,
                        vehicleId,
                        formMode,
                        onCancel,
                        renderButtonsPortalTarget,
                        dirty,
                        ...formLogicState,
                    })
                )}
            </AttachmentsLogic>
        </AppContext>
    )
}

export default flowRight(
    graphql(getEvent, {
        skip: ({ initEvent }) => !initEvent.id,
        options: ({ initEvent }) => ({
            variables: {
                id: initEvent.id,
            },
            fetchPolicy: fetchPolicy.NETWORK_ONLY,
        }),
    }),
)(EventFormLogic)
